/**
 * The kinds of class there are. No PIPELINE any more: a pipeline entry is the request that
 * precedes a class rather than a class of its own, so it is a separate entity with its own
 * endpoint - see model/PipelineKlass.
 *
 * A pipeline request still carries one of these, in its own `type`, to say which kind of class
 * it is asking to have created.
 */
export enum KLASS_TYPE {
    OPEN_ENROLMENT= 'OPEN_ENROLMENT',
    PRIVATE = 'PRIVATE',
    AD_HOC = 'AD_HOC',
};

export enum KLASS_MODALITY {
    IN_PERSON = 'IN_PERSON',
    VIRTUAL = 'VIRTUAL',
    RHLS_PREMIUM = 'RHLS_PREMIUM',
}

export enum KLASS_STATUS {
    CANCELLED = 'CANCELLED',
    CONFIRMED = 'CONFIRMED',
    DELIVERED = 'DELIVERED',
    GUARANTEED_TO_RUN = 'GUARANTEED_TO_RUN',
    MOVED = 'MOVED',
    ON_HOLD = 'ON_HOLD',
    PENDING = 'PENDING',
    PIPELINE = 'PIPELINE',
    SCHEDULED = 'SCHEDULED',
    TENTATIVE = 'TENTATIVE',
}

/**
 * The Red Hat operating unit a class was sold out of, as the Classes Outlook report reports it.
 *
 * Not derivable from the class's country, which is the near miss worth stating: the country says
 * where the class is delivered, this says which unit sold the offering. A virtual class has no
 * country at all, and the report leaves the unit blank for RHLS classes, so a class carrying no
 * region is normal rather than incomplete.
 */
export enum REGION {
    EMEA = 'EMEA',
    NAM = 'NAM',
    APAC = 'APAC',
    LATAM = 'LATAM',
}

export enum KLASS_HARDWARE {
    LOCAL = 'LOCAL',
    MCR = 'MCR',  // Mobile Classroom
    BYOD = 'BYOD', // Bring your own device
}

export enum KLASS_DECISION {
    PENDING = 'PENDING',
    CONFIRMED = 'CONFIRMED',
    CANCELLED = 'CANCELLED',
    PUBLISHED = 'PUBLISHED',
    UNPUBLISHED = 'UNPUBLISHED',
    NON_NIIT_DELIVERY = 'NON_NIIT_DELIVERY',
    DELIVERED = 'DELIVERED',
    COMMUNICATED = 'COMMUNICATED',
}

export enum PAYMENT_MODE {
    PURCHASE_ORDER = 'PURCHASE_ORDER',
    TRAINING_UNITS = 'TRAINING_UNITS',
}

/**
 * An ISO 4217 currency code - `USD`, `EUR`, `INR` - and not a union of the few we have seen.
 *
 * The service holds a `java.util.Currency` rather than an enum of its own, so every code the
 * standard has is a value that can arrive; the four-entry table this replaced named the name and
 * symbol of each, and threw on anything else. `currencyName` and `formatMoney` ask Intl for both
 * instead, which knows all of them and their decimals.
 */
export type CurrencyCode = string;

export enum HARDWARE_LEVEL {
    NOT_APPLICABLE = 'NOT_APPLICABLE',
    I = 'I',
    II = 'II',
    III = 'III',
    IV = 'IV',
    V = 'V',
    VI = 'VI',
    VII = 'VII',
    VT_ONLY = 'VT_ONLY'
}

export const HARDWARE_WEIGHTS: Record<HARDWARE_LEVEL, number> = {
  [HARDWARE_LEVEL.NOT_APPLICABLE]: 0,
  [HARDWARE_LEVEL.VT_ONLY]: 1,
  [HARDWARE_LEVEL.I]: 2,
  [HARDWARE_LEVEL.II]: 3,
  [HARDWARE_LEVEL.III]: 4,
  [HARDWARE_LEVEL.IV]: 5,
  [HARDWARE_LEVEL.V]: 6,
  [HARDWARE_LEVEL.VI]: 7,
  [HARDWARE_LEVEL.VII]: 8,
};

/**
 * Where a request has got to, on both halves of the ad hoc flow: the pipeline entry's own
 * `requestStatus`, and on the class it produced its `requestStatus` and `tssApproval`.
 *
 * SUBMMITTED is spelt the way the service spells it. These are wire values, so the typo is part
 * of the contract and correcting it here would simply stop matching.
 */
export enum REQUEST_STATUS {
    PENDING = 'PENDING',
    SUBMMITTED = 'SUBMMITTED',
    APPROVED = 'APPROVED',
    DECLINED = 'DECLINED',
    CANCELLED = 'CANCELLED',
}

/**
 * Who delivers a class: NIIT, Red Hat, or a partner.
 *
 * On every class rather than only an ad hoc one - the ad hoc request names the party that will
 * deliver, the LMS names the party that does, and they are the same question.
 */
export enum DELIVERY_RESPONSIBILITY {
    NIIT = 'NIIT',
    NON_NIIT = 'NON_NIIT',
    RED_HAT = 'RED_HAT',
}

/** Who raised an ad hoc request. */
export enum ADHOC_REQUESTER {
    PARTNER = 'PARTNER',
    TSS = 'TSS',
}

/**
 * How far a booking has got on an incoming ad hoc message.
 *
 * Import-only: the service reads it off the Kafka payload for what it implies about the class -
 * facility confirmed, instructors requested - and does not store it, so it never comes back on any
 * REST response. Kept here because it is part of the ad hoc contract and a reader of the import
 * needs the values; nothing in this application receives one.
 */
export enum ADHOC_BOOKING {
    PENDING = 'PENDING',
    DATE_LANG_CHANGE_REQUESTED = 'DATE_LANG_CHANGE_REQUESTED',
    CONFIRMED = 'CONFIRMED',
}

/** Whether the LMS has an offering for an ad hoc class yet. Import-only, as ADHOC_BOOKING is. */
export enum LMS_OFFERING_STATUS {
    NOT_CREATED = 'NOT_CREATED',
    UNPUBLISHED = 'UNPUBLISHED',
    PUBLISHED = 'PUBLISHED',
}

export enum INSTRUCTOR_TYPE {
    FTE = 'FTE',
    REDHAT = 'REDHAT',
    PARTNER = 'PARTNER',
    FREELANCER = 'FREELANCER',
} 

export enum INSTRUCTOR_STATUS {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
    PENDING = 'PENDING',
    REDHAT = 'REDHAT',
    NOT_ENROLLED = 'NOT_ENROLLED',
}

export enum SKILL_LEVEL {
    EXPERT = 'EXPERT',
    ADVANCED = 'ADVANCED',
    EXPERIENCED = 'EXPERIENCED',
    FOUNDATIONAL = 'FOUNDATIONAL',
    UNSKILLED = 'UNSKILLED',
    NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export enum AutomationMessageType {
  ERROR = 'ERROR',
  WARNING = 'WARNING',
  INFO = 'INFO'
}

/**
 * Which system found the problem an automation message is about - a different axis from its type,
 * which is how loud it is.
 *
 * This is the only field that still says where a message came from. The two systems that write the
 * panel used to say it in the entity name, each naming a class after the class it mapped the row
 * into - the sheet import filed one under `KlassDTO` and this service filed the very same class
 * under `Klass` - so an operator filtering on an entity saw one system's findings and never the
 * other's. The service now resolves every writer's name down to one, and this carries what the
 * name used to.
 *
 * Part of a message's identity there: the table is uniquely indexed on entity, key and source
 * together. So one class can hold a row under each source, and that is not duplication - the two
 * findings are about different things, and are fixed by different hands.
 */
export enum AutomationMessageSource {
  /**
   * The automation stack - the Smartsheet imports. Its findings are about the sheet: a cell nobody
   * can read, a column whose value is not one of the choices. Also how a message carrying no source
   * is read, that stack having been the only thing that ever wrote these.
   */
  AUTOMATION = 'AUTOMATION',

  /**
   * The service itself, mapping an imported row onto the model. Its findings are about the model: a
   * name that matched no master data, an offering id that is already somebody else's.
   */
  CAS = 'CAS'
}

export enum CLIENT_DELIVERY {
    CLIENT_DELIVERY = 'CLIENT_DELIVERY',
    STEP_IN = 'STEP_IN',
    ADMIN_FEE = 'ADMIN_FEE',
}

/** How loud a notification is. Same three values, and meanings, as AutomationMessageType. */
export enum NOTIFICATION_SEVERITY {
    ERROR = 'ERROR',
    WARNING = 'WARNING',
    INFO = 'INFO',
}

/**
 * What raised a notification — a different axis from its severity, which is how loud it is.
 *
 * Derived by the service from the notification's type rather than stored, so it is absent from
 * anything the websocket pushes: the envelope carries the type and this is not part of it.
 */
export enum NOTIFICATION_CATEGORY {
    SYSTEM = 'SYSTEM',
    BUSINESS = 'BUSINESS',
    ANNOUNCEMENT = 'ANNOUNCEMENT',
    REMINDER = 'REMINDER',
}

/** What a notification's audience value names. Null value for ALL, which is every authenticated user. */
export enum AUDIENCE_SCOPE {
    USER = 'USER',
    ROLE = 'ROLE',
    ALL = 'ALL',
}